import React from "react";
import { format } from "date-fns";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  VEDAMAAYU_HOSPITAL_ADDRESS,
  VEDAMAAYU_HOSPITAL_DESC,
  VEDAMAAYU_HOSPITAL_NAME,
} from "../shared/constants";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
const qz = require("qz-tray");

const value = (val) => (val === undefined || val === null || val === "" ? " " : String(val));

const patientName = (patientData) =>
  patientData?.fullName ||
  [patientData?.fname, patientData?.mname, patientData?.lname].filter(Boolean).join(" ") ||
  "";

const cell = (text, options = {}) => ({
  text: value(text),
  fontSize: options.fontSize || 8.5,
  bold: Boolean(options.bold),
  alignment: options.alignment,
  margin: options.margin || [3, 3, 3, 3],
  border: options.border || [true, true, true, true],
  colSpan: options.colSpan,
  fillColor: options.fillColor,
});

const labelCell = (text) => cell(text, { bold: true, fillColor: "#eeeeee" });

const section = (title) => [
  cell(title, { bold: true, colSpan: 4, fillColor: "#eeeeee", margin: [4, 3, 4, 3] }),
  {},
  {},
  {},
];

const vitalPair = (label1, value1, label2, value2) => [
  labelCell(label1),
  cell(value1),
  labelCell(label2),
  cell(value2),
];

const fullRow = (label, noteValue, height = 20) => [
  {
    colSpan: 4,
    margin: [4, 4, 4, height],
    stack: [
      { text: label, bold: true, fontSize: 8.5 },
      { text: value(noteValue), fontSize: 8.5, margin: [0, 3, 0, 0] },
    ],
  },
  {},
  {},
  {},
];

const printPdf = (docDefinition) => {
  pdfMake.createPdf(docDefinition).open();

  const printerName = process.env.REACT_APP_PRINTER_NAME;
  if (!printerName) return;

  pdfMake.createPdf(docDefinition).getBase64((data) => {
    qz.websocket
      .connect()
      .then(() => qz.printers.find())
      .then((printers) => {
        const printer = printers.find((p) => p.includes(printerName));
        if (!printer) throw new Error("Printer not found");
        const config = qz.configs.create(printer, {
          size: { width: 148, height: 210 },
          units: "mm",
        });
        return qz.print(config, [
          { type: "pixel", format: "pdf", flavor: "base64", data },
        ]);
      })
      .then(() => qz.websocket.disconnect())
      .catch(console.error);
  });
};

export function openNursePrint(notes, patientData) {
  const todayStr = format(new Date(), "dd-MM-yyyy");
  const ipOpNumber = patientData?.visit?.ipopnumber || patientData?.ipopnumber;
  const visitType = patientData?.visit?.type || patientData?.visitType || "";
  const ipOpLabel = /^OP/i.test(ipOpNumber || "") || /outpatient/i.test(visitType)
    ? "OP No."
    : "IP No.";
  const painScore = notes["Pain Score"];
  const painBoxes = [...Array(11).keys()].map((n) => ({
    text: String(n),
    alignment: "center",
    fontSize: 8,
    color: painScore === String(n) ? "#ffffff" : "#000000",
    fillColor: painScore === String(n) ? "#333333" : "#ffffff",
    margin: [0, 3, 0, 3],
  }));

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [28, 24, 28, 24],
    content: [
      {
        stack: [
          { text: VEDAMAAYU_HOSPITAL_NAME, color: "#176fad", bold: true, fontSize: 8 },
          { text: VEDAMAAYU_HOSPITAL_DESC, color: "#176fad", bold: true, fontSize: 7 },
          { text: VEDAMAAYU_HOSPITAL_ADDRESS, fontSize: 7 },
        ],
        margin: [0, 0, 0, 8],
      },
      {
        text: "NURSING INITIAL ASSESSMENT",
        alignment: "center",
        bold: true,
        fontSize: 11,
        margin: [0, 0, 0, 7],
      },
      {
        table: {
          widths: ["34%", "33%", "33%"],
          body: [
            [
              cell(`Patient Name: ${patientName(patientData)}`, { bold: true }),
              cell(`Age/Sex: ${value(patientData?.age)}/${value(patientData?.sex || patientData?.gender)}`, { bold: true }),
              cell(`Main: ${value(patientData?.uhid || patientData?.id)}`, { bold: true }),
            ],
            [
              cell(`${ipOpLabel}: ${value(ipOpNumber)}`, { bold: true }),
              cell(`Date: ${todayStr}`, { bold: true }),
              cell(`Ward: ${value(patientData?.ward)}`, { bold: true }),
            ],
          ],
        },
      },
      {
        table: {
          widths: ["22%", "28%", "22%", "28%"],
          body: [
            section("Vitals"),
            vitalPair("BP", notes["BP"], "Saturation (if required)", notes["Saturation"]),
            vitalPair("Temperature", notes["Temperature"], "Height", notes["Height"]),
            vitalPair("Pulse", notes["Pulse"], "Weight", notes["Weight"]),
            vitalPair("Respiratory Rate", notes["Respiratory Rate"], "GRBS", notes["GRBS"]),
            section("Examination"),
            fullRow(
              "Level of Consciousness / Respiratory Status / Present Chief Complaint",
              `${value(notes["Level of Consciousness"])}\n${value(notes["Respiratory Status"])}\n${value(notes["Nurse Chief Complaint"])}`,
              18
            ),
            fullRow("Skin Integrity", notes["Skin Integrity"], 12),
            section("Current Medications"),
            fullRow("", notes["Current Medications"], 18),
            section("Investigation Ordered"),
            fullRow("", notes["Investigation Ordered"], 14),
            section("Diet"),
            fullRow("", notes["Diet"], 14),
            section("Special Care"),
            fullRow(
              `Vulnerable: ${value(notes["Vulnerable Nurse"])}`,
              `Any Special Care Given: ${value(notes["Special Care"])}`,
              14
            ),
            section("Pain Scale"),
            [
              {
                colSpan: 4,
                stack: [
                  {
                    text: "Pain Score (0 = No pain    5 = Moderate    10 = Worst pain)",
                    bold: true,
                    fontSize: 8.5,
                    margin: [4, 4, 4, 4],
                  },
                  {
                    table: {
                      widths: Array(11).fill("*"),
                      body: [painBoxes],
                    },
                    margin: [4, 0, 4, 2],
                  },
                  {
                    columns: [
                      { text: "No pain", fontSize: 8 },
                      { text: "Moderate pain", alignment: "center", fontSize: 8 },
                      { text: "Worst pain", alignment: "right", fontSize: 8 },
                    ],
                    margin: [4, 0, 4, 4],
                  },
                ],
              },
              {},
              {},
              {},
            ],
            section("Pressure Sores & Restraints"),
            fullRow(
              `Pressure Sores: ${value(notes["Pressure Sores"])}`,
              `If yes give description (site, size, etc.): ${value(notes["Pressure Sores Description"])}`,
              14
            ),
            fullRow(`Restraints Used: ${value(notes["Restraints Used"])}`, `Restraint Used: ${value(notes["Restraint Description"])}`, 14),
            [
              cell("Name & Signature of the Nurse with Date & Time:", {
                bold: true,
                colSpan: 4,
                margin: [4, 8, 4, 34],
              }),
              {},
              {},
              {},
            ],
          ],
        },
        margin: [0, 6, 0, 0],
      },
    ],
    defaultStyle: { fontSize: 8.5 },
  };

  printPdf(docDefinition);
}

export default function VedamaayuNursePrint({ notes, patientData }) {
  React.useEffect(() => {
    if (notes && patientData) {
      openNursePrint(notes, patientData);
    }
  }, [notes, patientData]);

  return null;
}
